import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type {
  TicketLabelAdmin,
  TicketPriority,
  TicketSettings,
  TicketStaffView,
  TicketTraceStatus,
} from '@traceio-org/platform-contracts';
import type { TicketStaffUpdateInput } from '@traceio-org/platform-client';
import { MenuChecklist } from '../../components/MenuChecklist';
import { MenuSelect, type MenuOption } from '../../components/MenuSelect';
import { productName } from '../../format';
import { DueChip, LabelChip, PriorityIcon, StatusDot } from './TicketList';
import {
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  TRACE_STATUS_LABELS,
  describeTarget,
  staffStatusMoveAllowed,
} from './ticketFormat';

/** The property menus the page opens from the keyboard (S, P, A, L). */
export type PropertyMenu = 'status' | 'priority' | 'assignee' | 'labels';

const UNASSIGNED = '';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="admin-ticket-prop">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * The ticket's properties, in a column of the ticket page itself (never a drawer): status, priority, assignee and
 * labels as menus for staff who work tickets (read-only text for everyone else), then the customer, app, requester
 * and help desk, the links (Linear chips and other tickets), the followers with a Follow / Unfollow toggle, and the
 * reply clock (Reply in…, red Overdue…) with the response target for its priority. Each change is sent at once and the page reads the ticket again.
 */
export function TicketProperties({
  ticket,
  now,
  canManage,
  labels,
  assignees,
  settings,
  openMenu,
  onOpenMenu,
  onChange,
  onAddLink,
  onRemoveLink,
}: {
  ticket: TicketStaffView;
  /** The clock of the reply-due chip. */
  now: number;
  canManage: boolean;
  /** Every label (archived ones stay checked on tickets that carry them, but cannot be added). */
  labels: readonly TicketLabelAdmin[];
  /** Who the ticket can be assigned to (the caller, and the staff list when the caller may read it). */
  assignees: readonly MenuOption<string>[];
  settings: TicketSettings | null;
  openMenu: PropertyMenu | null;
  onOpenMenu: (menu: PropertyMenu | null) => void;
  onChange: (change: TicketStaffUpdateInput, description: string) => void;
  onAddLink: () => void;
  onRemoveLink: (linkId: string, ref: string) => void;
}) {
  const menu = (name: PropertyMenu) => ({
    open: openMenu === name,
    onOpenChange: (open: boolean) => onOpenMenu(open ? name : null),
  });
  const status = ticket.traceStatus;
  // The moves the server refuses from here (Triage always; out of a closed status, all but Todo and In progress) are
  // shown but disabled.
  const statusOptions = status
    ? STATUS_OPTIONS.map((o) => ({ ...o, disabled: !staffStatusMoveAllowed(status, o.value) }))
    : STATUS_OPTIONS;
  const labelIds = ticket.labels.map((l) => l.id);
  // Live labels, plus the archived ones this ticket already carries (so they can be taken off).
  const labelOptions = labels
    .filter((l) => !l.archivedAt || labelIds.includes(l.id))
    .map((l) => ({ value: l.id, label: l.archivedAt ? `${l.name} (archived)` : l.name, color: l.color }));
  const assigneeOptions: MenuOption<string>[] = [{ value: UNASSIGNED, label: 'Unassigned' }, ...assignees];
  if (ticket.traceAssignee?.id && !assignees.some((a) => a.value === ticket.traceAssignee!.id)) {
    assigneeOptions.push({
      value: ticket.traceAssignee.id,
      label: ticket.traceAssignee.name ?? 'Staff member',
    });
  }
  const target = settings?.responseTargets[ticket.priority as Exclude<TicketPriority, 'none'>];

  return (
    <aside className="admin-ticket-props" aria-label="Ticket properties">
      <dl>
        <Row label="Status">
          {canManage && status ? (
            <MenuSelect
              label="Status"
              showLabel={false}
              value={status}
              options={statusOptions}
              onChange={(next: TicketTraceStatus) =>
                next !== status && onChange({ traceStatus: next }, `Status: ${TRACE_STATUS_LABELS[next]}.`)
              }
              icon={<StatusDot status={status} />}
              {...menu('status')}
            />
          ) : (
            <span className="admin-ticket-prop__value">
              <StatusDot status={status} />
              {status ? TRACE_STATUS_LABELS[status] : 'Not with Trace'}
            </span>
          )}
        </Row>
        <Row label="Priority">
          {canManage ? (
            <MenuSelect
              label="Priority"
              showLabel={false}
              value={ticket.priority}
              options={PRIORITY_OPTIONS}
              onChange={(next: TicketPriority) =>
                next !== ticket.priority &&
                onChange({ priority: next }, `Priority: ${PRIORITY_LABELS[next]}.`)
              }
              icon={<PriorityIcon priority={ticket.priority} />}
              {...menu('priority')}
            />
          ) : (
            <span className="admin-ticket-prop__value">
              <PriorityIcon priority={ticket.priority} />
              {PRIORITY_LABELS[ticket.priority]}
            </span>
          )}
        </Row>
        <Row label="Assignee">
          {canManage ? (
            <MenuSelect
              label="Assignee"
              showLabel={false}
              value={ticket.traceAssignee?.id ?? UNASSIGNED}
              options={assigneeOptions}
              onChange={(next: string) => {
                if (next === (ticket.traceAssignee?.id ?? UNASSIGNED)) return;
                const name = assigneeOptions.find((o) => o.value === next)?.label ?? 'them';
                onChange(
                  { staffAssigneeUserId: next === UNASSIGNED ? null : next },
                  next === UNASSIGNED ? 'Unassigned.' : `Assigned to ${name}.`,
                );
              }}
              {...menu('assignee')}
            />
          ) : (
            <span className="admin-ticket-prop__value">{ticket.traceAssignee?.name ?? 'Unassigned'}</span>
          )}
        </Row>
        <Row label="Labels">
          {canManage ? (
            <MenuChecklist
              label="Labels"
              values={labelIds}
              options={labelOptions}
              emptyText="Add labels"
              summary={
                ticket.labels.length > 0 ? (
                  <span className="admin-ticket-labels">
                    {ticket.labels.map((label) => (
                      <LabelChip key={label.id} label={label} />
                    ))}
                  </span>
                ) : undefined
              }
              onToggle={(value, checked) => {
                const next = checked ? [...labelIds, value] : labelIds.filter((id) => id !== value);
                const name = labels.find((l) => l.id === value)?.name ?? 'Label';
                onChange({ labelIds: next }, checked ? `Label added: ${name}.` : `Label removed: ${name}.`);
              }}
              {...menu('labels')}
            />
          ) : ticket.labels.length > 0 ? (
            <span className="admin-ticket-labels">
              {ticket.labels.map((label) => (
                <LabelChip key={label.id} label={label} />
              ))}
            </span>
          ) : (
            <span className="admin-muted">None</span>
          )}
        </Row>
        <Row label="Customer">
          <Link to={`/organizations/${ticket.organizationId}`}>
            {ticket.organizationName || 'Open the organization'}
          </Link>
        </Row>
        <Row label="App">
          {ticket.product ? (
            productName(ticket.product)
          ) : (
            <span className="admin-muted">Not sure / account</span>
          )}
        </Row>
        <Row label="Requester">{ticket.requester.name ?? <span className="admin-muted">Unknown</span>}</Row>
        <Row label="Help desk">
          {ticket.assignee?.name ?? <span className="admin-muted">Unassigned</span>}
        </Row>
        <Row label="Links">
          <ul className="admin-bare-list admin-ticket-links">
            {ticket.links.map((link) => (
              <li key={link.id}>
                {link.kind === 'linear' && link.url ? (
                  <a
                    className="admin-link-chip"
                    data-kind="linear"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.ref}
                  </a>
                ) : (
                  <Link className="admin-link-chip" to={`/tickets/${link.ref}`}>
                    {link.kind === 'duplicate_of' ? `Duplicate of ${link.ref}` : `Related: ${link.ref}`}
                  </Link>
                )}
                {canManage ? (
                  <button
                    type="button"
                    className="admin-link-button"
                    aria-label={`Remove link ${link.ref}`}
                    onClick={() => onRemoveLink(link.id, link.ref)}
                  >
                    ×
                  </button>
                ) : null}
              </li>
            ))}
            {canManage ? (
              <li>
                <button type="button" className="admin-link-button" onClick={onAddLink}>
                  + Link
                </button>
              </li>
            ) : null}
            {!canManage && ticket.links.length === 0 ? <li className="admin-muted">None</li> : null}
          </ul>
        </Row>
        <Row label="Followers">
          <span className="admin-ticket-follow">
            {ticket.subscribers.length > 0 ? (
              <span>{ticket.subscribers.map((p) => p.displayName ?? 'Someone').join(', ')}</span>
            ) : (
              <span className="admin-muted">Nobody</span>
            )}
            <button
              type="button"
              className="admin-link-button"
              onClick={() =>
                onChange(
                  { subscribe: !ticket.subscribed },
                  ticket.subscribed
                    ? `You no longer follow ${ticket.number}.`
                    : `You follow ${ticket.number}.`,
                )
              }
            >
              {ticket.subscribed ? 'Unfollow' : 'Follow'}
            </button>
          </span>
        </Row>
      </dl>
      <div className="admin-ticket-props__foot">
        <p className="admin-ticket-props__due">
          First reply: <DueChip item={ticket} now={now} />
          {!ticket.responseDueAt && !ticket.firstTraceReplyAt ? <span>no target</span> : null}
        </p>
        <p>
          Response target ({PRIORITY_LABELS[ticket.priority]}):{' '}
          <strong>{settings ? describeTarget(target) : 'Loading'}</strong> for the first Trace reply.
        </p>
        <p className="admin-muted">Keyboard: S status · P priority · A assign · L labels</p>
      </div>
    </aside>
  );
}
